import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Play, RotateCcw, Users, Award, ClipboardCheck, ChevronRight, Trophy, Plus, Pencil, Trash2, List, Download } from 'lucide-react';

export function QCMPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const API = process.env.REACT_APP_BACKEND_URL;

  const [view, setView] = useState(isAdmin ? 'admin' : 'quiz');
  const [employes, setEmployes] = useState([]);
  const [resultats, setResultats] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quiz state
  const [session, setSession] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [quizFinished, setQuizFinished] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [selectedEmploye, setSelectedEmploye] = useState('');
  const [detailDialog, setDetailDialog] = useState(null);

  // Question management state
  const [questionDialog, setQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [qForm, setQForm] = useState({ question: '', categorie: '', options: ['', '', '', ''], reponse: 0 });
  const [adminTab, setAdminTab] = useState('resultats');
  const [filterCat, setFilterCat] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, qRes, catRes] = await Promise.allSettled([
        api.get('/rh/employes'),
        api.get('/qcm/questions'),
        api.get('/qcm/categories'),
      ]);
      if (empRes.status === 'fulfilled') setEmployes(empRes.value.data);
      if (qRes.status === 'fulfilled') setQuestions(qRes.value.data);
      if (catRes.status === 'fulfilled') setCategories(catRes.value.data);

      if (isAdmin) {
        try {
          const resRes = await api.get('/qcm/resultats');
          setResultats(resRes.data);
        } catch {}
      }
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [api, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startQuiz = async (employeId, employeNom) => {
    try {
      const res = await api.post('/qcm/sessions', { employe_id: employeId, employe_nom: employeNom });
      setSession(res.data);
      setCurrentIndex(0);
      setSelectedAnswer(null);
      setAnswered(false);
      setAnswerCorrect(null);
      setCorrectAnswer(null);
      setQuizFinished(false);
      setFinalScore(null);
      setView('quiz');
    } catch {
      toast.error('Erreur création session');
    }
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null || !session) return;
    try {
      const res = await api.post(`/qcm/sessions/${session.id}/answer`, {
        question_id: questions[currentIndex].id,
        reponse: selectedAnswer,
      });
      setAnswered(true);
      setAnswerCorrect(res.data.correct);
      setCorrectAnswer(res.data.reponse_correcte);
    } catch {
      toast.error('Erreur');
    }
  };

  const nextQuestion = () => {
    if (currentIndex + 1 >= questions.length) {
      finishQuiz();
    } else {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setAnswered(false);
      setAnswerCorrect(null);
      setCorrectAnswer(null);
    }
  };

  const finishQuiz = async () => {
    try {
      const res = await api.post(`/qcm/sessions/${session.id}/finish`);
      setFinalScore(res.data);
      setQuizFinished(true);
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const viewDetail = async (sessionData) => {
    try {
      const res = await api.get(`/qcm/sessions/${sessionData.id}`);
      setDetailDialog(res.data);
    } catch {
      toast.error('Erreur');
    }
  };

  // Question CRUD
  const openAddQuestion = () => {
    setEditingQuestion(null);
    setQForm({ question: '', categorie: '', options: ['', '', '', ''], reponse: 0 });
    setQuestionDialog(true);
  };

  const openEditQuestion = (q) => {
    setEditingQuestion(q);
    setQForm({ question: q.question, categorie: q.categorie, options: [...q.options], reponse: q.reponse });
    setQuestionDialog(true);
  };

  const saveQuestion = async () => {
    const { question, categorie, options, reponse } = qForm;
    if (!question.trim() || !categorie.trim() || options.some(o => !o.trim())) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      if (editingQuestion) {
        await api.put(`/qcm/questions/${editingQuestion.id}`, { question, categorie, options, reponse });
        toast.success('Question modifiée');
      } else {
        await api.post('/qcm/questions', { question, categorie, options, reponse });
        toast.success('Question ajoutée');
      }
      setQuestionDialog(false);
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const deleteQuestion = async (qId) => {
    if (!window.confirm('Supprimer cette question ?')) return;
    try {
      await api.delete(`/qcm/questions/${qId}`);
      toast.success('Question supprimée');
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const downloadAttestation = (sessionId) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/qcm/sessions/${sessionId}/pdf?token=${token}`, '_blank');
  };

  // Group results by employee
  const resultsByEmployee = employes.map(emp => {
    const empResults = resultats.filter(r => r.employe_id === emp.id);
    const best = empResults.length > 0 ? Math.max(...empResults.map(r => r.score_pct || 0)) : null;
    const last = empResults[0] || null;
    return { ...emp, results: empResults, best, last, count: empResults.length };
  });

  if (loading) return <div className="flex items-center justify-center h-96"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  // QUIZ VIEW
  if (view === 'quiz' && session && !quizFinished) {
    const q = questions[currentIndex];
    const progress = ((currentIndex + (answered ? 1 : 0)) / questions.length) * 100;
    const catLabel = q?.categorie || '';

    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="qcm-quiz-view">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">QCM Formation</h1>
            <p className="text-sm text-muted-foreground">{session.employe_nom}</p>
          </div>
          <Badge variant="outline" className="text-base px-3 py-1">
            {currentIndex + 1} / {questions.length}
          </Badge>
        </div>

        <Progress value={progress} className="h-2" data-testid="qcm-progress" />

        <Card>
          <CardHeader className="pb-3">
            <Badge className="w-fit mb-2" variant="secondary">{catLabel}</Badge>
            <CardTitle className="text-lg" data-testid="qcm-question-text">{q?.question}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {q?.options.map((opt, i) => {
              let cls = 'border-2 p-4 rounded-lg cursor-pointer transition-all text-left w-full ';
              if (answered) {
                if (i === correctAnswer) cls += 'border-green-500 bg-green-50 dark:bg-green-900/20 ';
                else if (i === selectedAnswer && !answerCorrect) cls += 'border-red-500 bg-red-50 dark:bg-red-900/20 ';
                else cls += 'border-muted opacity-50 ';
              } else {
                cls += i === selectedAnswer
                  ? 'border-primary bg-primary/5 '
                  : 'border-muted hover:border-primary/50 ';
              }

              return (
                <button
                  key={i}
                  className={cls}
                  onClick={() => !answered && setSelectedAnswer(i)}
                  disabled={answered}
                  data-testid={`qcm-option-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-sm">
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-sm font-medium">{opt}</span>
                    {answered && i === correctAnswer && <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto" />}
                    {answered && i === selectedAnswer && !answerCorrect && i !== correctAnswer && <XCircle className="w-5 h-5 text-red-500 ml-auto" />}
                  </div>
                </button>
              );
            })}

            <div className="flex justify-end gap-3 pt-4">
              {!answered ? (
                <Button onClick={submitAnswer} disabled={selectedAnswer === null} data-testid="qcm-validate-btn">
                  Valider
                </Button>
              ) : (
                <Button onClick={nextQuestion} data-testid="qcm-next-btn">
                  {currentIndex + 1 >= questions.length ? 'Terminer' : 'Question suivante'}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // RESULTS VIEW
  if (view === 'quiz' && quizFinished && finalScore) {
    const scorePct = finalScore.score_pct;
    const scoreColor = scorePct >= 80 ? 'text-green-600' : scorePct >= 50 ? 'text-amber-600' : 'text-red-600';
    const scoreLabel = scorePct >= 80 ? 'Excellent !' : scorePct >= 50 ? 'Peut mieux faire' : 'Insuffisant';

    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="qcm-results-view">
        <Card>
          <CardContent className="pt-8 text-center space-y-4">
            <Trophy className={`w-16 h-16 mx-auto ${scoreColor}`} />
            <h1 className="text-3xl font-bold">Résultats QCM</h1>
            <p className="text-muted-foreground">{session?.employe_nom}</p>
            <div className={`text-6xl font-bold ${scoreColor}`} data-testid="qcm-final-score">
              {scorePct}%
            </div>
            <p className={`text-xl font-semibold ${scoreColor}`}>{scoreLabel}</p>
            <p className="text-muted-foreground">{finalScore.score} / {finalScore.total} bonnes réponses</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Score par catégorie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(finalScore.scores_categories || {}).map(([cat, data]) => (
              <div key={cat}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium">{cat}</span>
                  <span className={`text-sm font-bold ${data.pct >= 80 ? 'text-green-600' : data.pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {data.correct}/{data.total} ({data.pct}%)
                  </span>
                </div>
                <Progress value={data.pct} className="h-2" />
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => downloadAttestation(session.id)} data-testid="qcm-download-attestation">
            <Download className="w-4 h-4 mr-2" /> Attestation PDF
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => { setView('admin'); setSession(null); setQuizFinished(false); }} data-testid="qcm-back-admin">
              Retour tableau de bord
            </Button>
          )}
          <Button onClick={() => { setSession(null); setQuizFinished(false); setView(isAdmin ? 'admin' : 'quiz'); }} data-testid="qcm-new-quiz">
            <RotateCcw className="w-4 h-4 mr-2" /> Nouveau QCM
          </Button>
        </div>
      </div>
    );
  }

  // START QUIZ VIEW (employee selection for non-admin OR admin launching for an employee)
  if (view === 'quiz' && !session) {
    return (
      <div className="max-w-lg mx-auto space-y-6" data-testid="qcm-start-view">
        <Card>
          <CardHeader className="text-center">
            <ClipboardCheck className="w-12 h-12 mx-auto text-primary mb-2" />
            <CardTitle>QCM Formation Chana Thai</CardTitle>
            <CardDescription>{questions.length} questions en {categories.length} catégories</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {categories.map(c => (
                <div key={c.nom} className="flex justify-between text-sm p-2 bg-muted/50 rounded">
                  <span>{c.nom}</span>
                  <Badge variant="outline">{c.nb_questions} questions</Badge>
                </div>
              ))}
            </div>
            <Separator />
            <div>
              <label className="text-sm font-medium mb-2 block">Sélectionner l'employé</label>
              <Select value={selectedEmploye} onValueChange={setSelectedEmploye}>
                <SelectTrigger data-testid="qcm-employe-select">
                  <SelectValue placeholder="Choisir un employé" />
                </SelectTrigger>
                <SelectContent>
                  {employes.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.prenom} - {e.poste}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full"
              size="lg"
              disabled={!selectedEmploye}
              onClick={() => {
                const emp = employes.find(e => e.id === selectedEmploye);
                if (emp) startQuiz(emp.id, emp.prenom);
              }}
              data-testid="qcm-start-btn"
            >
              <Play className="w-5 h-5 mr-2" /> Commencer le QCM
            </Button>
            {isAdmin && (
              <Button variant="outline" className="w-full" onClick={() => setView('admin')} data-testid="qcm-go-admin">
                Voir les résultats
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ADMIN DASHBOARD VIEW
  const filteredQuestions = filterCat === 'all' ? questions : questions.filter(q => q.categorie === filterCat);
  const allCatNames = [...new Set(questions.map(q => q.categorie))];

  return (
    <div className="space-y-6" data-testid="qcm-admin-view">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">QCM Formation</h1>
          <p className="text-muted-foreground text-sm">{questions.length} questions - {resultats.length} passage(s)</p>
        </div>
        <Button onClick={() => setView('quiz')} data-testid="qcm-launch-btn">
          <Play className="w-4 h-4 mr-2" /> Lancer un QCM
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Employés évalués</p><p className="text-2xl font-bold" data-testid="qcm-stat-evaluated">{resultsByEmployee.filter(e => e.count > 0).length} / {employes.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total passages</p><p className="text-2xl font-bold">{resultats.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Score moyen</p><p className="text-2xl font-bold" data-testid="qcm-stat-avg">{resultats.length > 0 ? (resultats.reduce((s, r) => s + (r.score_pct || 0), 0) / resultats.length).toFixed(1) : 0}%</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Meilleur score</p><p className="text-2xl font-bold text-green-600">{resultats.length > 0 ? Math.max(...resultats.map(r => r.score_pct || 0)) : 0}%</p></CardContent></Card>
      </div>

      {/* Tabs: Résultats / Questions */}
      <Tabs value={adminTab} onValueChange={setAdminTab}>
        <TabsList>
          <TabsTrigger value="resultats" data-testid="qcm-tab-resultats">
            <Users className="w-4 h-4 mr-1" /> Résultats
          </TabsTrigger>
          <TabsTrigger value="questions" data-testid="qcm-tab-questions">
            <List className="w-4 h-4 mr-1" /> Questions ({questions.length})
          </TabsTrigger>
        </TabsList>

        {/* RÉSULTATS TAB */}
        <TabsContent value="resultats" className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Résultats par employé</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employé</TableHead>
                    <TableHead>Poste</TableHead>
                    <TableHead className="text-center">Passages</TableHead>
                    <TableHead className="text-center">Meilleur</TableHead>
                    <TableHead className="text-center">Dernier</TableHead>
                    <TableHead className="text-center">Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resultsByEmployee.map(emp => (
                    <TableRow key={emp.id} data-testid={`qcm-emp-row-${emp.id}`}>
                      <TableCell className="font-medium">{emp.prenom}</TableCell>
                      <TableCell><Badge variant="outline">{emp.poste}</Badge></TableCell>
                      <TableCell className="text-center">{emp.count}</TableCell>
                      <TableCell className="text-center">
                        {emp.best !== null ? <Badge className={emp.best >= 80 ? 'bg-green-100 text-green-700' : emp.best >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{emp.best}%</Badge> : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {emp.last ? <Badge className={emp.last.score_pct >= 80 ? 'bg-green-100 text-green-700' : emp.last.score_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{emp.last.score_pct}%</Badge> : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-center text-sm">{emp.last ? new Date(emp.last.date).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {emp.last && <Button size="sm" variant="ghost" onClick={() => viewDetail(emp.last)} data-testid={`qcm-detail-${emp.id}`}>Détail</Button>}
                          <Button size="sm" variant="outline" onClick={() => startQuiz(emp.id, emp.prenom)} data-testid={`qcm-relaunch-${emp.id}`}><RotateCcw className="w-3 h-3 mr-1" /> Relancer</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {resultats.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Award className="w-5 h-5" /> Historique des passages</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Employé</TableHead>
                      <TableHead className="text-center">Score</TableHead>
                      <TableHead className="text-center">Résultat</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultats.slice(0, 20).map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="text-sm">{new Date(r.date).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell className="font-medium">{r.employe_nom}</TableCell>
                        <TableCell className="text-center"><span className="font-bold">{r.score}/{r.total}</span></TableCell>
                        <TableCell className="text-center">
                          <Badge className={r.score_pct >= 80 ? 'bg-green-100 text-green-700' : r.score_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{r.score_pct}%</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => viewDetail(r)}>Détail</Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadAttestation(r.id)} data-testid={`qcm-pdf-${r.id}`}><Download className="w-3 h-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* QUESTIONS TAB */}
        <TabsContent value="questions">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2"><List className="w-5 h-5" /> Banque de questions</CardTitle>
                <div className="flex gap-2">
                  <Select value={filterCat} onValueChange={setFilterCat}>
                    <SelectTrigger className="w-48" data-testid="qcm-filter-cat">
                      <SelectValue placeholder="Toutes catégories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes ({questions.length})</SelectItem>
                      {allCatNames.map(c => (
                        <SelectItem key={c} value={c}>{c} ({questions.filter(q => q.categorie === c).length})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={openAddQuestion} data-testid="qcm-add-question-btn">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="text-center">Réponse</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuestions.map((q, i) => (
                    <TableRow key={q.id} data-testid={`qcm-q-row-${q.id}`}>
                      <TableCell className="text-muted-foreground text-sm">{i + 1}</TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{q.question}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {q.options?.map((o, j) => (
                            <Badge key={j} variant={j === q.reponse ? 'default' : 'outline'} className={`text-xs ${j === q.reponse ? 'bg-green-100 text-green-800 border-green-300' : ''}`}>
                              {String.fromCharCode(65 + j)}: {o}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{q.categorie}</Badge></TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-green-100 text-green-700">{String.fromCharCode(65 + (q.reponse || 0))}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditQuestion(q)} data-testid={`qcm-edit-${q.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteQuestion(q.id)} data-testid={`qcm-delete-${q.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="qcm-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              <div className="flex justify-between items-center">
                <span>Détail - {detailDialog?.employe_nom} ({detailDialog?.score_pct}%)</span>
                {detailDialog && <Button size="sm" variant="outline" onClick={() => downloadAttestation(detailDialog.id)} data-testid="qcm-detail-pdf"><Download className="w-3 h-3 mr-1" /> PDF</Button>}
              </div>
            </DialogTitle>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Date :</span> {new Date(detailDialog.date).toLocaleDateString('fr-FR')}</div>
                <div><span className="text-muted-foreground">Score :</span> <span className="font-bold">{detailDialog.score}/{detailDialog.total}</span></div>
              </div>
              <Separator />
              {Object.entries(detailDialog.scores_categories || {}).map(([cat, data]) => (
                <div key={cat}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{cat}</span>
                    <span className={`text-sm font-bold ${data.pct >= 80 ? 'text-green-600' : data.pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{data.correct}/{data.total} ({data.pct}%)</span>
                  </div>
                  <Progress value={data.pct} className="h-2" />
                </div>
              ))}
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium">Réponses détaillées</p>
                {(detailDialog.reponses || []).map((r, i) => {
                  const q = questions.find(q => q.id === r.question_id);
                  return (
                    <div key={i} className={`flex items-start gap-2 p-2 rounded text-sm ${r.correct ? 'bg-green-50 dark:bg-green-900/10' : 'bg-red-50 dark:bg-red-900/10'}`}>
                      {r.correct ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                      <span className="text-muted-foreground">{q?.question || r.question_id}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Question Dialog */}
      <Dialog open={questionDialog} onOpenChange={setQuestionDialog}>
        <DialogContent className="max-w-lg" data-testid="qcm-question-dialog">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Modifier la question' : 'Ajouter une question'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Catégorie</Label>
              <Select value={qForm.categorie} onValueChange={v => setQForm(f => ({...f, categorie: v}))}>
                <SelectTrigger data-testid="qcm-form-cat">
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {allCatNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value="__new">+ Nouvelle catégorie...</SelectItem>
                </SelectContent>
              </Select>
              {qForm.categorie === '__new' && (
                <Input className="mt-2" placeholder="Nom de la catégorie" onChange={e => setQForm(f => ({...f, categorie: e.target.value}))} data-testid="qcm-form-newcat" />
              )}
            </div>
            <div>
              <Label>Question</Label>
              <Input value={qForm.question} onChange={e => setQForm(f => ({...f, question: e.target.value}))} placeholder="Tapez votre question ici" data-testid="qcm-form-question" />
            </div>
            {qForm.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <button
                  type="button"
                  className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-sm cursor-pointer transition-all ${i === qForm.reponse ? 'border-green-500 bg-green-100 text-green-700' : 'border-muted'}`}
                  onClick={() => setQForm(f => ({...f, reponse: i}))}
                  data-testid={`qcm-form-correct-${i}`}
                >
                  {String.fromCharCode(65 + i)}
                </button>
                <Input
                  value={opt}
                  onChange={e => { const opts = [...qForm.options]; opts[i] = e.target.value; setQForm(f => ({...f, options: opts})); }}
                  placeholder={`Réponse ${String.fromCharCode(65 + i)}`}
                  data-testid={`qcm-form-option-${i}`}
                />
                {qForm.options.length > 2 && (
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => {
                    const opts = qForm.options.filter((_, j) => j !== i);
                    const newReponse = qForm.reponse >= opts.length ? 0 : qForm.reponse > i ? qForm.reponse - 1 : qForm.reponse;
                    setQForm(f => ({...f, options: opts, reponse: newReponse}));
                  }}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {qForm.options.length < 6 && (
              <Button variant="outline" size="sm" onClick={() => setQForm(f => ({...f, options: [...f.options, '']}))} data-testid="qcm-form-add-option">
                <Plus className="w-3 h-3 mr-1" /> Ajouter une option
              </Button>
            )}
            <p className="text-xs text-muted-foreground">Cliquez sur la lettre (A, B, C...) pour marquer la bonne réponse.</p>
            <Button className="w-full" onClick={saveQuestion} data-testid="qcm-form-save">
              {editingQuestion ? 'Enregistrer les modifications' : 'Ajouter la question'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
